import { parsePaddingString } from '../../common-utils';
import { safeParse } from '../../common-utils/math';
import { copy } from '../../common-utils';
import { NodeLike } from '../context/node';
import { TYPES, TEXT_VERTICAL_ALIGN_TYPES } from '../constants';
import { getLineHeightInPx } from './text';
import { getNodeMatrix } from '../effects/matrix';

/**
 * Get padding of the node.
 * The priority is
 *  individual side properties > padding object > padding string/number > default 0
 * @param node
 * @returns
 */
export function getPaddingBox(node: NodeLike) {
  const paddingObj = node.padding;
  let pt = 0,
    pr = 0,
    pb = 0,
    pl = 0;

  if (typeof paddingObj === 'object' && paddingObj !== null) {
    const paddingObjTyped = paddingObj as {
      top?: number;
      right?: number;
      bottom?: number;
      left?: number;
    };
    pt = paddingObjTyped.top ?? 0;
    pr = paddingObjTyped.right ?? 0;
    pb = paddingObjTyped.bottom ?? 0;
    pl = paddingObjTyped.left ?? 0;
  } else if (typeof paddingObj === 'string' || typeof paddingObj === 'number') {
    [pt, pr, pb, pl] = parsePaddingString(paddingObj);
  }

  const left = safeParse(node.paddingLeft ?? pl);
  const right = safeParse(node.paddingRight ?? pr);
  const top = safeParse(node.paddingTop ?? pt);
  const bottom = safeParse(node.paddingBottom ?? pb);

  return {
    top,
    bottom,
    left,
    right,
    x: left + right,
    y: top + bottom,
  };
}

function removeHiddenProps(node, readed?: Set<unknown>) {
  if (!(node instanceof Object)) return node;
  readed = readed || new Set();
  if (readed.has(node)) {
    throw `readed: ${node}`;
  }
  readed.add(node);
  const newNode = { ...node };
  Object.keys(newNode).forEach((key) => {
    if (key.startsWith('_')) {
      delete newNode[key];
    } else if (newNode[key] instanceof Object) {
      if (Array.isArray(newNode[key])) {
        newNode[key] = newNode[key].map((item) => removeHiddenProps(item, readed));
      } else {
        newNode[key] = removeHiddenProps(newNode[key], readed);
      }
    }
  });
  return newNode;
}

function removeNodes(originNodes) {
  const nodes = {};
  if (!originNodes) return nodes;
  Object.keys(originNodes).map((id) => {
    nodes[id] = removeHiddenProps(originNodes[id]);
  });
  return nodes;
}

export function templateCopy(input) {
  const template = copy(input);
  delete template.props.input;
  return Object.assign({}, template, {
    props: {
      ...template.props,
      nodes: removeNodes(template.props.nodes),
    },
  });
}

export function sortByIndex(a: NodeLike, b: NodeLike) {
  if (a.index < b.index) {
    return -1;
  }
  if (a.index > b.index) {
    return 1;
  }
  return 0;
}

export function createId() {
  return Math.random().toString(36).substr(2, 9);
}

/**
 * 渲染边界接口
 */
export interface RenderBounds {
  x: number;
  y: number;
  w: number;
  h: number;
}

/**
 * 计算旋转后的轴对齐包围盒（AABB）
 * @param cx - 旋转中心 x
 * @param cy - 旋转中心 y
 * @param x - 原始矩形左上角 x
 * @param y - 原始矩形左上角 y
 * @param w - 原始矩形宽度
 * @param h - 原始矩形高度
 * @param matrix - 变换矩阵 [a, b, c, d, e, f]
 * @returns 旋转后的 AABB { x, y, w, h }
 */
function getRotatedBounds(
  cx: number,
  cy: number,
  x: number,
  y: number,
  w: number,
  h: number,
  matrix: number[]
): RenderBounds {
  const [a, b, c, d, e, f] = matrix;

  // 四个角点（相对于中心点）
  const corners = [
    { x: x - cx, y: y - cy }, // 左上
    { x: x + w - cx, y: y - cy }, // 右上
    { x: x + w - cx, y: y + h - cy }, // 右下
    { x: x - cx, y: y + h - cy }, // 左下
  ];

  // 应用变换矩阵到每个角点
  const transformed = corners.map((p) => ({
    x: a * p.x + c * p.y + e + cx,
    y: b * p.x + d * p.y + f + cy,
  }));

  // 计算 AABB
  const xs = transformed.map((p) => p.x);
  const ys = transformed.map((p) => p.y);
  const minX = Math.min(...xs);
  const maxX = Math.max(...xs);
  const minY = Math.min(...ys);
  const maxY = Math.max(...ys);

  return {
    x: minX,
    y: minY,
    w: maxX - minX,
    h: maxY - minY,
  };
}

/**
 * 计算节点的实际渲染边界（考虑文字溢出、旋转等情况）
 *
 * 对于文字节点，实际渲染区域可能超出 node.x/y/w/h：
 * 1. lineHeight 导致的 bleed（上下左右各扩展 lineHeight/2）
 * 2. 文字内容溢出（根据 alignItems 决定向上/向下/居中扩展）
 *
 * 对于有旋转的节点，计算旋转后的轴对齐包围盒（AABB）
 *
 * @param renderContext - 渲染上下文，需要包含 providers.textLayout
 * @param node - 节点对象
 * @returns 实际渲染边界 { x, y, w, h }
 */
export function getNodeRenderBounds(renderContext: any, node: any): RenderBounds {
  // 基础边界
  let bounds: RenderBounds = { x: node.x, y: node.y, w: node.w, h: node.h };

  // 文字节点需要考虑溢出
  if (node.type === TYPES.text && node.text) {
    const fontSize = node.fontSize || 12;
    const lineHeightRatio = node.lineHeight ?? 1.35;
    const lineHeight = getLineHeightInPx(fontSize, lineHeightRatio) * 0.5;

    // 计算文字实际高度
    const textLayoutProvider = renderContext?.providers?.textLayout;
    const textLayout = textLayoutProvider?.getTextLayout?.(node.id);
    const measuredHeight = textLayout?.height || node.h;
    const isOverHeight = measuredHeight > node.h;

    // 应用 lineHeight bleed
    bounds.x -= lineHeight / 2;
    bounds.y -= lineHeight / 2;
    bounds.w += lineHeight;
    bounds.h += lineHeight;

    // 处理文字溢出
    if (isOverHeight) {
      const delta = measuredHeight - node.h;
      const alignItems = node.alignItems || TEXT_VERTICAL_ALIGN_TYPES.top;

      switch (alignItems) {
        case TEXT_VERTICAL_ALIGN_TYPES.center:
          bounds.y -= delta / 2;
          bounds.h += delta;
          break;
        case TEXT_VERTICAL_ALIGN_TYPES.bottom:
          bounds.y -= delta;
          bounds.h += delta;
          break;
        default: // top
          bounds.h += delta;
          break;
      }
    }
  }

  // 检查是否有旋转变换
  const matrix = getNodeMatrix(node);
  if (matrix && Array.isArray(matrix) && matrix.length === 6) {
    // 检查是否不是单位矩阵（有实际变换）
    const isIdentity =
      Math.abs(matrix[0] - 1) < 1e-6 &&
      Math.abs(matrix[1]) < 1e-6 &&
      Math.abs(matrix[2]) < 1e-6 &&
      Math.abs(matrix[3] - 1) < 1e-6 &&
      Math.abs(matrix[4]) < 1e-6 &&
      Math.abs(matrix[5]) < 1e-6;

    if (!isIdentity) {
      // 计算旋转中心（节点中心）
      const nodeX = node.w < 0 ? node.x + node.w : node.x;
      const nodeY = node.h < 0 ? node.y + node.h : node.y;
      const nodeW = Math.abs(node.w);
      const nodeH = Math.abs(node.h);
      const cx = nodeX + nodeW / 2;
      const cy = nodeY + nodeH / 2;

      // 计算旋转后的 AABB
      bounds = getRotatedBounds(cx, cy, bounds.x, bounds.y, bounds.w, bounds.h, matrix);
    }
  }

  return bounds;
}
